import React, { useEffect, useState, useCallback } from 'react';
import { auditApi } from '../api/client';

// yyyy-mm-dd for N days ago (UTC), used for the time-summary window.
function daysAgo(n: number): string {
  const d = new Date(Date.now() - n * 86400000);
  return d.toISOString().slice(0, 10);
}
function fmtDuration(mins: number): string {
  if (!mins || mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const ACTION_COLORS: Record<string, { bg: string; color: string }> = {
  login: { bg: '#dbeafe', color: '#1d4ed8' },
  create: { bg: '#d1fae5', color: '#065f46' },
  update: { bg: '#fef3c7', color: '#92400e' },
  delete: { bg: '#fee2e2', color: '#991b1b' },
};

function actionStyle(action: string) {
  const key = Object.keys(ACTION_COLORS).find(k => (action || '').startsWith(k));
  return key ? ACTION_COLORS[key] : { bg: '#f3f4f6', color: '#374151' };
}

export default function ActivityLog() {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [objectTypeFilter, setObjectTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  // Time-in-System summary
  const [timeRange, setTimeRange] = useState<'today' | '7d' | '30d'>('7d');
  const [timeSummary, setTimeSummary] = useState<any[]>([]);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeError, setTimeError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = { limit: '300' };
      if (userFilter) params.user_id = userFilter;
      if (actionFilter) params.action = actionFilter;
      if (objectTypeFilter) params.object_type = objectTypeFilter;
      const res = await auditApi.list(params);
      setEvents(res.items || res.events || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load activity log. Auditor or administrator access is required.');
    }
    setLoading(false);
  }, [userFilter, actionFilter, objectTypeFilter]);

  useEffect(() => { load(); }, [load]);

  const loadTimeSummary = useCallback(async () => {
    setTimeLoading(true); setTimeError('');
    try {
      const from = timeRange === 'today' ? daysAgo(0) : timeRange === '7d' ? daysAgo(6) : daysAgo(29);
      const to = daysAgo(0);
      const res = await auditApi.timeSummary({ from, to });
      setTimeSummary(res.summary || []);
    } catch (e: any) {
      setTimeError(e.message || 'Failed to load time summary');
    }
    setTimeLoading(false);
  }, [timeRange]);

  useEffect(() => { loadTimeSummary(); }, [loadTimeSummary]);

  // Distinct values for filter dropdowns
  const users = Array.from(new Set(events.map(e => e.username || e.user_id).filter(Boolean))).sort();
  const actions = Array.from(new Set(events.map(e => e.action).filter(Boolean))).sort();
  const objectTypes = Array.from(new Set(events.map(e => e.object_type).filter(Boolean))).sort();

  let filtered = events;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(e =>
      (e.username || '').toLowerCase().includes(s) ||
      (e.action || '').toLowerCase().includes(s) ||
      (e.object_type || '').toLowerCase().includes(s) ||
      (e.object_id || '').toLowerCase().includes(s) ||
      (e.detail || '').toLowerCase().includes(s)
    );
  }

  const fmtTime = (e: any) => {
    if (e.timestamp_iso) return new Date(e.timestamp_iso).toLocaleString();
    if (e.timestamp) return new Date(Number(e.timestamp) * 1000).toLocaleString();
    return '—';
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: '#374151', background: '#f9fafb', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
  const sel: React.CSSProperties = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 };

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Activity Log</h2>
        <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Who did what and when. Records logins and every create, update, and delete across the system.</p>
      </div>

      {/* ── Time in System (active use, reconstructed from presence heartbeats) ── */}
      <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: 15, margin: 0, color: '#1f2937' }}>Time in System</h3>
            <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Active time per user (heartbeat-based; reflects active use, not idle tab time).</p>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {([['today', 'Today'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setTimeRange(k)} style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', borderRadius: 6,
                border: `1px solid ${timeRange === k ? '#2563eb' : '#d1d5db'}`,
                background: timeRange === k ? '#eff6ff' : 'white', color: timeRange === k ? '#1d4ed8' : '#6b7280',
              }}>{label}</button>
            ))}
          </div>
        </div>
        {timeError && <div style={{ fontSize: 12, color: '#dc2626', padding: 8 }}>{timeError}</div>}
        {timeLoading ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
        ) : timeSummary.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            No active-time data for this window yet. It populates as users spend time in the system.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>User</th>
                <th style={{ ...th, textAlign: 'right' }}>Active Time</th>
                <th style={{ ...th, textAlign: 'right' }}>Sessions</th>
                <th style={{ ...th, textAlign: 'right' }}>Events</th>
                <th style={th}>Last Active</th>
              </tr>
            </thead>
            <tbody>
              {timeSummary.map((r: any) => (
                <tr key={r.user_id}>
                  <td style={{ ...td, fontWeight: 500, color: '#1f2937' }}>{r.username || r.user_id}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#1d4ed8' }}>{fmtDuration(r.active_minutes)}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#374151' }}>{r.sessions}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#6b7280' }}>{r.events}</td>
                  <td style={{ ...td, color: '#6b7280', whiteSpace: 'nowrap' }}>{r.last_active_iso ? new Date(r.last_active_iso).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search user, action, object, detail..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search activity"
        />
        <select style={sel} value={userFilter} onChange={e => setUserFilter(e.target.value)} aria-label="Filter by user">
          <option value="">All users</option>
          {users.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select style={sel} value={actionFilter} onChange={e => setActionFilter(e.target.value)} aria-label="Filter by action">
          <option value="">All actions</option>
          {actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select style={sel} value={objectTypeFilter} onChange={e => setObjectTypeFilter(e.target.value)} aria-label="Filter by object type">
          <option value="">All types</option>
          {objectTypes.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {(userFilter || actionFilter || objectTypeFilter || search) && (
          <button onClick={() => { setUserFilter(''); setActionFilter(''); setObjectTypeFilter(''); setSearch(''); }} style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Clear</button>
        )}
        <button onClick={load} style={{ padding: '8px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>

      {error && <div role="alert" style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', color: '#6b7280', fontSize: 14 }}>
          No activity recorded yet.
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Time</th>
                <th style={th}>User</th>
                <th style={th}>Role</th>
                <th style={th}>Action</th>
                <th style={th}>Object</th>
                <th style={th}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e: any) => {
                const s = actionStyle(e.action);
                return (
                  <tr key={e.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#6b7280' }}>{fmtTime(e)}</td>
                    <td style={{ ...td, fontWeight: 500, color: '#1f2937' }}>{e.username || e.user_id || 'system'}</td>
                    <td style={{ ...td, color: '#6b7280' }}>{e.user_role || '—'}</td>
                    <td style={td}>
                      <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 600, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{e.action}</span>
                    </td>
                    <td style={{ ...td, color: '#374151' }}>
                      <div>{e.object_type || '—'}</div>
                      {e.object_id && <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>{String(e.object_id).slice(0, 20)}</div>}
                    </td>
                    <td style={{ ...td, color: '#6b7280', maxWidth: 360 }}>{e.detail || (e.new_value ? String(e.new_value).slice(0, 120) : '—')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af' }}>Showing {filtered.length} of {events.length} recent events.</div>
    </div>
  );
}
